import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Chip,
  Button,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Build as MaintenanceIcon,
  Cancel as InactiveIcon,
  DirectionsCar as CarIcon,
  Person as DriverIcon,
  Event as CalendarIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Payments as PaymentsIcon,
  CreditCard as CreditCardIcon,
  MonetizationOn as MonetizationOnIcon,
  PaymentOutlined as PayPalIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { fetchVehicleById, deleteVehicle, addMaintenanceRecord, releaseVehicle } from '../../redux/slices/vehiclesSlice';
import { fetchShifts } from '../../redux/slices/shiftsSlice';
import { getVehicleTypeLabel, getVehicleTypeSeats, getCapabilityLabel } from '../../config/vehicleTypes';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

// Styled Components
const VehicleIcon = styled(CarIcon)(({ theme }) => ({
  width: theme.spacing(15),
  height: theme.spacing(15),
  color: theme.palette.primary.main,
  marginRight: theme.spacing(4),
}));

const DetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let colorName;
  
  switch (status) {
    case 'active': // Fahrzeug ist verfügbar (inUse=false)
      colorName = 'success'; // Grün für verfügbare Fahrzeuge
      break;
    case 'maintenance':
      colorName = 'warning'; // Orange für Fahrzeuge in Wartung
      break;
    case 'in_use': // Fahrzeug ist in Benutzung (inUse=true)
      colorName = 'primary'; // Blau für Fahrzeuge in Benutzung
      break;
    case 'inactive':
    default:
      colorName = 'default'; // Grau für inaktive oder unbekannte Zustände
      break;
  }
  
  // Sicherstellen, dass theme.palette[colorName] existiert
  const colorExists = theme.palette && theme.palette[colorName];
  const backgroundColor = (status === 'inactive' || !colorExists) ? 
    undefined : 
    theme.palette[colorName].main;
  
  return {
    color: (status === 'inactive' || !colorExists) ? 'inherit' : 'white',
    backgroundColor: backgroundColor,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const ShiftStatusChip = styled(Chip)(({ theme, status }) => {
  let color = 'default';
  
  switch (status) {
    case 'completed':
      color = 'success';
      break;
    case 'cancelled':
      color = 'error';
      break;
    case 'active':
      color = 'primary';
      break;
    case 'scheduled':
      color = 'info';
      break;
    default:
      color = 'default';
  }
  
  return {
    color: 'white',
    backgroundColor: theme.palette[color].main,
    '& .MuiChip-label': {
      fontSize: '0.75rem',
    },
  };
});

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentVehicle, isLoading, error } = useSelector(state => state.vehicles);
  const { shifts } = useSelector(state => state.shifts);
  
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState({
    description: '',
    odometer: '',
    cost: '',
    notes: ''
  });
  
  useEffect(() => {
    if (id) {
      dispatch(fetchVehicleById(id));
      
      // Auch die Schichten für dieses Fahrzeug holen
      dispatch(fetchShifts({ vehicleId: id }));
    }
  }, [dispatch, id]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    dispatch(deleteVehicle(id)).then(() => {
      navigate('/fahrzeuge');
    });
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleMaintenanceDialogOpen = () => {
    setMaintenanceDialogOpen(true);
  };
  
  const handleMaintenanceDialogClose = () => {
    setMaintenanceDialogOpen(false);
    setMaintenanceData({
      description: '',
      odometer: '',
      cost: '',
      notes: ''
    });
  };
  
  const handleMaintenanceChange = (e) => {
    const { name, value } = e.target;
    setMaintenanceData({
      ...maintenanceData,
      [name]: value
    });
  };
  
  const handleMaintenanceSubmit = () => {
    dispatch(addMaintenanceRecord({
      vehicleId: id,
      record: {
        description: maintenanceData.description,
        odometer: parseInt(maintenanceData.odometer) || 0,
        cost: parseFloat(maintenanceData.cost) || 0,
        notes: maintenanceData.notes
      }
    }));
    
    handleMaintenanceDialogClose();
  };
  
  const handleReleaseDialogOpen = () => {
    setReleaseDialogOpen(true);
  };
  
  const handleReleaseDialogClose = () => {
    setReleaseDialogOpen(false);
  };
  
  const handleReleaseConfirm = () => {
    dispatch(releaseVehicle(id));
    setReleaseDialogOpen(false);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'active': 'Verfügbar', // Fahrzeug ist verfügbar (inUse=false)
      'in_use': 'Im Einsatz', // Fahrzeug wird verwendet (inUse=true)
      'maintenance': 'Wartung',
      'inactive': 'Inaktiv',
      'scheduled': 'Geplant',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
      'online': 'Online',
      'offline': 'Offline',
      'available': 'Verfügbar'
    };
    
    return statusMap[status] || status;
  };
  
  const getVehicleShifts = () => {
    return shifts.filter(shift => shift.vehicle_id === id);
  };
  
  if (isLoading) {
    return <Loading message="Fahrzeug wird geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Fahrzeugs"
        message={error}
        onRetry={() => dispatch(fetchVehicleById(id))}
      />
    );
  }
  
  if (!currentVehicle) {
    return (
      <ErrorMessage
        title="Fahrzeug nicht gefunden"
        message="Das angeforderte Fahrzeug konnte nicht gefunden werden."
        onRetry={() => navigate('/fahrzeuge')}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Fahrzeugdetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrzeuge', path: '/fahrzeuge' },
          { label: currentVehicle.model || 'Fahrzeug', path: `/fahrzeuge/${id}` }
        ]}
        actionButton={{
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/fahrzeuge/${id}/bearbeiten`
        }}
      />
      
      {/* Fahrzeug Übersicht */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
              <VehicleIcon />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h4" component="h1">
                    {currentVehicle.model}
                  </Typography>
                  <StatusChip 
                    label={translateStatus(currentVehicle.status)}
                    status={currentVehicle.status}
                    size="medium"
                    icon={
                      currentVehicle.status === 'active' ? <ActiveIcon /> : 
                      currentVehicle.status === 'maintenance' ? <MaintenanceIcon /> : 
                      currentVehicle.status === 'in_use' ? <DriverIcon /> : 
                      <InactiveIcon />
                    }
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Fahrzeugnummer: {currentVehicle.vehicleNumber || 'Nicht vergeben'}
                </Typography>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Kennzeichen: {currentVehicle.licensePlate}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {currentVehicle.current_driver ? (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<DriverIcon />}
                      component={Link}
                      to={`/fahrer/${currentVehicle.current_driver.id}`}
                    >
                      Fahrer: {currentVehicle.current_driver.user?.name || 'Unbekannt'}
                    </Button>
                  ) : (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<DriverIcon />}
                      disabled
                    >
                      Kein Fahrer zugewiesen
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
              {currentVehicle.inUse && currentVehicle.current_driver && (
                <Button 
                  variant="contained"
                  color="primary" 
                  onClick={handleReleaseDialogOpen}
                  sx={{ mr: 1 }}
                >
                  Fahrzeug freigeben
                </Button>
              )}
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<MaintenanceIcon />}
                onClick={handleMaintenanceDialogOpen}
                sx={{ mr: 1 }}
              >
                Wartung hinzufügen
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                sx={{ mr: 1 }}
                disabled={currentVehicle.inUse}
              >
                Löschen
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* KPIs/Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrzeugdaten</Typography>
              </Box>
              <Typography variant="body1">TÜV bis: {currentVehicle.tuvDate ? formatDate(currentVehicle.tuvDate) : 'Nicht angegeben'}</Typography>
              <Typography variant="body1">
                Fahrzeugnummer: {currentVehicle.vehicleNumber || 'Nicht vergeben'}
              </Typography>
              <Typography variant="body1">
                Typ: {getVehicleTypeLabel(currentVehicle.vehicleType)} ({getVehicleTypeSeats(currentVehicle.vehicleType)} Sitzplätze)
              </Typography>
              {currentVehicle.specialCapabilities && currentVehicle.specialCapabilities.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {currentVehicle.specialCapabilities.map(cap => (
                    <Chip
                      key={cap}
                      label={getCapabilityLabel(cap)}
                      size="small"
                      sx={{ backgroundColor: '#9c27b0', color: 'white' }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                Inspektion am: {currentVehicle.inspectionDate ? formatDate(currentVehicle.inspectionDate) : 'Nicht angegeben'}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MaintenanceIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Wartung</Typography>
              </Box>
              <Typography variant="body1">
                Letzte Wartung: {formatDate(currentVehicle.last_maintenance) || 'Keine Wartung verzeichnet'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Nächste Wartung: {formatDate(currentVehicle.next_maintenance) || 'Nicht geplant'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Anzahl Wartungen: {currentVehicle.maintenanceHistory?.length || 0}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DriverIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrer</Typography>
              </Box>
              <Typography variant="body1">
                {currentVehicle.current_driver ? 
                  currentVehicle.current_driver.user?.name : 
                  'Kein Fahrer zugewiesen'}
              </Typography>
              {currentVehicle.current_driver && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    Status: {translateStatus(currentVehicle.current_driver.isOnline ? 'online' : 'offline')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Telefon: {currentVehicle.current_driver.phone || currentVehicle.current_driver.user?.phone || 'Nicht angegeben'}
                  </Typography>
                </>
              )}
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Aktivität</Typography>
              </Box>
              <Typography variant="body1">
                Erstellt am: {formatDate(currentVehicle.created_at) || formatDate(currentVehicle.lastActivityTimestamp)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Zuletzt aktualisiert: {formatDate(currentVehicle.lastActivityTimestamp) || 'Keine Aktualisierung'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Anzahl Schichten: {getVehicleShifts().length || 0}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
      </Grid>
      
      {/* Tabs für Wartungen und Schichten */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Wartungshistorie" />
          <Tab label="Schichten" />
          <Tab label="Ausstattung & Zahlungsoptionen" />
        </Tabs>
        
        {/* Wartungshistorie Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Wartungshistorie
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleMaintenanceDialogOpen}
            >
              Wartung hinzufügen
            </Button>
          </Box>
          
          <List>
            {/* Zugriff auf den korrekten Feldnamen für die Wartungshistorie */}
            {currentVehicle.maintenanceHistory && currentVehicle.maintenanceHistory.length > 0 ? (
              [...currentVehicle.maintenanceHistory]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((record, index) => (
                <Paper key={record.id || index} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {record.description || `Wartung #${index + 1}`}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(record.date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Kilometerstand: {record.odometer ? `${record.odometer} km` : 'Nicht angegeben'}
                    </Typography>
                    <Typography variant="body2">
                      Kosten: {record.cost ? `${record.cost.toFixed(2)} €` : 'Keine Angabe'}
                    </Typography>
                  </Box>
                  {record.notes && (
                    <Typography variant="body2">
                      Notizen: {record.notes}
                    </Typography>
                  )}
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Wartungshistorie für dieses Fahrzeug gefunden.
              </Typography>
            )}
          </List>
        </TabPanel>
        
        {/* Schichten Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Schichten mit diesem Fahrzeug
          </Typography>
          <List>
            {getVehicleShifts().length > 0 ? (
              getVehicleShifts().slice(0, 5).map((shift) => (
                <Paper key={shift.id} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {formatDate(shift.start_time)} ({formatDateTime(shift.start_time)} - {formatDateTime(shift.end_time)})
                    </Typography>
                    <ShiftStatusChip
                      label={translateStatus(shift.status)}
                      status={shift.status}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Fahrer: {shift.driver ? (
                        <Link to={`/fahrer/${shift.driver.id}`}>
                          {shift.driver.user?.name || 'Unbekannt'}
                        </Link>
                      ) : 'Kein Fahrer'}
                    </Typography>
                    <Typography variant="body2">
                      Fahrten: {shift.total_rides || 0} | Einnahmen: {shift.total_earnings ? `${shift.total_earnings.toFixed(2)} €` : '0.00 €'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      component={Link}
                      to={`/schichten/${shift.id}`}
                    >
                      Details anzeigen
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Schichten für dieses Fahrzeug gefunden.
              </Typography>
            )}
          </List>
          {getVehicleShifts().length > 5 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                component={Link} 
                to={`/schichten?vehicleId=${id}`}
              >
                Alle Schichten anzeigen
              </Button>
            </Box>
          )}
        </TabPanel>
        
        {/* Ausstattung & Zahlungsoptionen Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Ausstattungsmerkmale
              </Typography>
              <Box sx={{ mb: 3 }}>
                {currentVehicle.features && currentVehicle.features.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentVehicle.features.map((feature, index) => (
                      <Chip 
                        key={index} 
                        label={feature} 
                        color="primary" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1">
                    Keine besonderen Ausstattungsmerkmale hinterlegt.
                  </Typography>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Zahlungsmöglichkeiten
              </Typography>
              <Paper elevation={1} sx={{ p: 2 }}>
                {currentVehicle.paymentMethods === 1 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOnIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography>Nur Barzahlung</Typography>
                  </Box>
                ) : currentVehicle.paymentMethods === 2 ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                      <Typography>Barzahlung</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCardIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                      <Typography>EC/Kreditkartenzahlung</Typography>
                    </Box>
                  </Box>
                ) : currentVehicle.paymentMethods === 3 ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MonetizationOnIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                      <Typography>Barzahlung</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CreditCardIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                      <Typography>EC/Kreditkartenzahlung</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PayPalIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                      <Typography>PayPal-Zahlung</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography>Keine Zahlungsmöglichkeiten hinterlegt</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrzeug löschen"
        content={`Möchten Sie das Fahrzeug ${currentVehicle.model} (${currentVehicle.license_plate}) wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      
      {/* Fahrzeug freigeben Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleReleaseDialogClose} maxWidth="sm">
        <DialogTitle>Fahrzeug freigeben</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Möchten Sie das Fahrzeug {currentVehicle?.model} ({currentVehicle?.licensePlate}) wirklich freigeben?
            </Typography>
            <Typography variant="body2" color="error">
              Hinweis: Der Fahrer {currentVehicle?.current_driver?.user?.name} wird dadurch von diesem Fahrzeug abgemeldet.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReleaseDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleReleaseConfirm} 
            variant="contained" 
            color="primary"
          >
            Fahrzeug freigeben
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Wartung hinzufügen Dialog */}
      <Dialog open={maintenanceDialogOpen} onClose={handleMaintenanceDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Wartung hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Beschreibung"
              name="description"
              value={maintenanceData.description}
              onChange={handleMaintenanceChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Kilometerstand"
              name="odometer"
              type="number"
              value={maintenanceData.odometer}
              onChange={handleMaintenanceChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Kosten (€)"
              name="cost"
              type="number"
              value={maintenanceData.cost}
              onChange={handleMaintenanceChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Notizen"
              name="notes"
              multiline
              rows={3}
              value={maintenanceData.notes}
              onChange={handleMaintenanceChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMaintenanceDialogClose}>Abbrechen</Button>
          <Button 
            onClick={handleMaintenanceSubmit} 
            variant="contained" 
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!maintenanceData.description}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleDetails;
